import { z } from "zod";
import { reportZodValidationError } from "./monitoring";

export interface ZodErrorContext {
    operation: string;
    endpoint?: string;
    data?: unknown;
    expectedSchema?: string;
    // Defaults to true for API parsing errors. Safe parsing wrappers disable it explicitly.
    reportToMonitoring?: boolean;
}

export interface ZodErrorDetails {
    path: string;
    message: string;
    code: string;
    received: unknown;
    expected?: string;
}

/**
 * Formate les erreurs Zod en un format lisible avec contexte
 */
export const formatZodError = (error: z.ZodError, context: ZodErrorContext): string => {
    const { operation, endpoint, expectedSchema } = context;
    const schemaLabel = expectedSchema !== undefined ? ` [${expectedSchema}]` : "";

    const summary = `🔴 Zod Validation Error in ${operation}${endpoint ? ` (${endpoint})` : ""}${schemaLabel} - Found ${error.issues.length} validation error(s)`;

    return summary;
};

/**
 * Parse avec Zod et log les erreurs de manière détaillée
 */
export const zodParseWithDetailedLog = <T>(data: unknown, schema: z.ZodSchema<T>, context: ZodErrorContext): T => {
    try {
        return schema.parse(data);
    } catch (error) {
        if (error instanceof z.ZodError) {
            const summary = formatZodError(error, context);
            console.error(summary);

            // Toujours afficher le tableau des erreurs pour un debug facile
            const tableData = error.issues.map((issue) => ({
                Path: issue.path.join(".") || "root",
                Message: issue.message,
                Code: issue.code,
                Received: "received" in issue ? (JSON.stringify(issue.received) ?? "unknown") : "unknown",
                Expected: "expected" in issue ? String(issue.expected) : "N/A",
            }));
            console.table(tableData);

            if (context.reportToMonitoring !== false) {
                reportZodValidationError(error, {
                    operation: context.operation,
                    endpoint: context.endpoint,
                    expectedSchema: context.expectedSchema,
                    summary,
                    issueCount: error.issues.length,
                    issues: tableData.map((issue) => ({
                        path: issue.Path,
                        message: issue.Message,
                        code: issue.Code,
                        received: issue.Received,
                        expected: issue.Expected,
                    })),
                });
            }
        }
        throw error;
    }
};

/**
 * Parse avec Zod de manière safe (retourne null en cas d'erreur)
 */
export const zodSafeParse = <T>(data: unknown, schema: z.ZodSchema<T>, context: ZodErrorContext): T | null => {
    try {
        return zodParseWithDetailedLog(data, schema, {
            ...context,
            reportToMonitoring: false,
        });
    } catch {
        return null;
    }
};

/**
 * Valide un array d'éléments avec Zod et filtre les éléments invalides
 */
export const zodParseArray = <T>(dataArray: unknown[], itemSchema: z.ZodSchema<T>, context: ZodErrorContext): T[] => {
    const validItems: T[] = [];
    const invalidCount = { count: 0 };

    dataArray.forEach((item, index) => {
        const itemContext: ZodErrorContext = {
            ...context,
            operation: `${context.operation}[${index}]`,
        };

        const parsed = zodSafeParse(item, itemSchema, itemContext);
        if (parsed !== null) {
            validItems.push(parsed);
        } else {
            invalidCount.count++;
        }
    });

    if (invalidCount.count > 0) {
        console.warn(`Filtered out ${invalidCount.count} invalid items from array`);
    }

    return validItems;
};

/**
 * Crée un wrapper pour les appels API avec validation Zod automatique
 */
export const createZodApiWrapper = <TResponse>(responseSchema: z.ZodSchema<TResponse>, operation: string) => {
    return async (apiCall: () => Promise<{ data: unknown }>): Promise<TResponse> => {
        try {
            const response = await apiCall();
            return zodParseWithDetailedLog(response.data, responseSchema, {
                operation,
                endpoint: "API response",
            });
        } catch (error) {
            // Si c'est une erreur Zod, on la re-lance
            if (error instanceof z.ZodError) {
                throw error;
            }
            // Sinon on log l'erreur API et on la re-lance
            throw error;
        }
    };
};

/**
 * Utilitaire pour logger les schémas Zod pour le debugging
 */
export const logZodSchema = (schema: z.ZodSchema, schemaName: string): void => {
    if (process.env.NODE_ENV === "development") {
        console.group(`📋 Schema: ${schemaName}`);
        try {
            // Essayer de créer un exemple valide
            const example = schema.safeParse({});
            if (example.success) {
                console.info("Schema is valid");
            } else {
                example.error.issues.forEach((issue) => {
                    console.error(`Issue: ${issue.message}`);
                });
            }
        } catch (error) {
            console.error("Error in logZodSchema:", error);
        }
        console.groupEnd();
    }
};
